import alfrescoFileService from "@/services/AlfrescoFileService.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import configService from "@/services/ConfigService.js";
import { useConfigStore } from "@/stores/config";
import { useUserStore } from "@/stores/user";
import viewConfigService from "./ViewConfigService.js";

import i18n from "@/i18n/index.js";

const t = i18n.global.t;

const CONFIG_FILENAME = "env-config.json";
const SAVED_VIEWS_FILENAME = "savedViews.json";

const OPTS = {
  include: [
    "aspectNames",
    "isFavorite",
    "properties",
    "allowableOperations",
    "permissions",
    "path",
    "isLocked",
  ],
  maxItems: 500,
  skipCount: 0,
};

/**
 * Service for managing user views, both personal and global.
 */
class UserViewService {
  /**
   * Retrieves the user's hidden 'Configuration' folder.
   * If the folder doesn't exist and `createIfMissing` is true, it will be created.
   * @param {string} userId - The ID of the user.
   * @param {boolean} [createIfMissing=false] - Whether to create the folder if it's not found.
   * @returns {Promise<Object|null>} The hidden folder node object, or null if not found and not created.
   * @private
   */
  async _getHiddenFolder(userId, createIfMissing = false) {
    const homeFolderResponse =
      await alfrescoNodeService.getHomeFolderFromUser(userId);
    const homeFolderId =
      homeFolderResponse.list.entries[0].entry.properties["cm:homeFolder"];
    if (!homeFolderId) {
      throw new Error(t("vuesUtilisateurs.noUserHomeFound"));
    }

    const homeFolderChildren = await alfrescoNodeService.getChildrenWithOpts(
      homeFolderId,
      OPTS,
      false,
    );
    let hiddenFolder = homeFolderChildren.children.find(
      (entry) => entry.aspectNames && entry.aspectNames.includes("pm:hidden"),
    );

    if (!hiddenFolder && createIfMissing) {
      console.warn("No hidden folder found, creating one...");
      const createdNode = await this.createConfigFolder(homeFolderId);
      hiddenFolder = createdNode.entry;
    }

    return hiddenFolder;
  }

  /**
   * Retrieves or creates the 'savedViews.json' file within the user's hidden configuration folder.
   * @param {string} hiddenFolderId - The ID of the hidden configuration folder.
   * @param {boolean} [createIfMissing=false] - Whether to create the file if it's not found.
   * @returns {Promise<Object|null>} The file node object, or null if not found and not created.
   * @private
   */
  async _getOrCreateViewsFile(hiddenFolderId, createIfMissing = false) {
    const hiddenChildrenResponse =
      await alfrescoNodeService.getChildrenWithOpts(hiddenFolderId, OPTS, true);
    let viewsFile = hiddenChildrenResponse.children.find(
      (f) => f.name === SAVED_VIEWS_FILENAME,
    );
    if (!viewsFile && createIfMissing) {
      console.warn(`No ${SAVED_VIEWS_FILENAME} file found, creating one...`);
      const fileOpts = {
        name: SAVED_VIEWS_FILENAME,
        nodeType: "cm:content",
      };
      const initialStructure = {
        personalSavedView: [],
        globalFavoriteView: [],
      };
      const initialContent = new Blob(
        [JSON.stringify(initialStructure, null, 2)],
        { type: "application/json" },
      );
      const createdFileNode = await alfrescoFileService.uploadFile(
        hiddenFolderId,
        fileOpts,
        initialContent,
      );

      const nodeToUpdate = createdFileNode.entry;
      const updatedNode = await alfrescoNodeService.updateNode(
        nodeToUpdate.id,
        {
          aspectNames: [...(nodeToUpdate.aspectNames || []), "pm:hidden"],
        },
      );
      viewsFile = updatedNode.entry;
    }
    return viewsFile;
  }

  async getContent(nodeId, versionId, attachment) {
    try {
      return await alfrescoNodeService.getVersionContent(
        nodeId,
        versionId,
        attachment,
      );
    } catch (err) {
      // This.$toast is not available in a service, consider emitting an event or returning an error
      console.error("Error getting content:", err.message);
      throw err; // Re-throw to be handled by the caller
    }
  }

  /**
   * Parses the JSON content of a given file node.
   * @returns {Promise<Object>} The parsed JSON object.
   * @private
   */
  async _parseFileContent(file) {
    let fileText;
    try {
      const fileContent = await this.getContent(
        file.id,
        file.properties["cm:versionLabel"],
        true,
      );
      const blobToText = (blob) =>
        new Promise((resolve, reject) => {
          if (!(blob instanceof Blob)) {
            resolve(blob);
            return;
          }
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
          reader.readAsText(blob, "utf-8");
        });
      fileText = await blobToText(fileContent);
    } catch (error) {
      console.warn(
        `Could not get content for node ${file.id}, assuming empty.`,
        error.message,
      );
      fileText = "";
    }
    if (!fileText || fileText.trim() === "") {
      return { personalSavedView: [], globalFavoriteView: [] };
    }

    try {
      return JSON.parse(fileText);
    } catch (parseError) {
      console.error("Error parsing JSON content:", parseError);
      return { personalSavedView: [], globalFavoriteView: [] };
    }
  }

  /**
   * Normalizes the saved views file content to the latest object format.
   * This handles backward compatibility for the old array-based format.
   * @param {Object|Array} fileContent - The raw content from the saved views file.
   * @returns {{personalSavedView: Array, globalFavoriteView: Array}} The normalized content.
   * @private
   */
  _normalizeFileContent(fileContent) {
    if (Array.isArray(fileContent)) {
      return {
        personalSavedView: fileContent,
        globalFavoriteView: [],
      };
    }
    // Ensure the main properties exist even if the file is malformed
    return {
      personalSavedView: fileContent?.personalSavedView || [],
      globalFavoriteView: fileContent?.globalFavoriteView || [],
    };
  }

  /**
   * Charge le contenu brut du fichier des vues enregistrées pour l'utilisateur courant.
   * Vérifie l'existence de l'utilisateur, du dossier caché et du fichier de vues.
   * @private
   * @returns {Promise<Object|null>} Le contenu du fichier parsé ou `null` si absent.
   * @throws {Error} Si l'utilisateur n'est pas trouvé.
   */
  async _loadViewsFileContent() {
    const user = useUserStore().person;
    if (!user) {
      throw new Error(t("vuesUtilisateurs.userNotFound"));
    }

    const hiddenFolder = await this._getHiddenFolder(user.id, false);
    if (!hiddenFolder) {
      return null;
    }

    const viewsFile = await this._getOrCreateViewsFile(hiddenFolder.id, false);
    if (!viewsFile) {
      return null;
    }
    return this._parseFileContent(viewsFile);
  }

  /**
   * Récupère la liste des vues personnelles de l'utilisateur courant.
   * Utilise le fichier de configuration stocké dans le dossier caché.
   * @returns {Promise<Array>} Tableau d'objets représentant les vues sauvegardées.
   * Renvoie `[]` si aucune vue n'est trouvée ou en cas d'erreur.
   */
  async getPersonalViews() {
    try {
      const fileContent = await this._loadViewsFileContent();
      if (!fileContent) {
        return [];
      }
      return fileContent?.personalSavedView || [];
    } catch (error) {
      console.error("Error loading personal views:", error.message);
      return [];
    }
  }

  /**
   * Récupère l'intégralité du fichier de configuration des vues de l'utilisateur,
   * comprenant à la fois les vues personnelles et les vues favorites globales.
   * @returns {Promise<Object>} Un objet contenant deux propriétés :
   *  - `personalSavedView`: {Array} liste des vues personnelles
   *  - `globalFavoriteView`: {Array} liste des vues globales favorites
   * Renvoie un objet vide si aucune donnée n'est trouvée ou en cas d'erreur.
   */
  async getPersonalViewsAndGlobalFavorites() {
    try {
      const fileContent = await this._loadViewsFileContent();
      if (!fileContent) {
        return { personalSavedView: [], globalFavoriteView: [] };
      }

      return this._normalizeFileContent(fileContent);
    } catch (error) {
      console.error("Error loading personal views:", error.message);
      return { personalSavedView: [], globalFavoriteView: [] };
    }
  }

  /**
   * Retrieves all global views from vue-*.json config files.
   * Falls back to SAVED_VIEWS_GLOBAL from env-config.json for backward compatibility.
   * @returns {Promise<Array>} A list of global saved views.
   */
  async getGlobalViews() {
    try {
      const filenames = await viewConfigService.listViews();
      if (filenames.length === 0) {
        // Fallback to legacy SAVED_VIEWS_GLOBAL
        const configStore = useConfigStore();
        return configStore.SAVED_VIEWS_GLOBAL || [];
      }
      const views = await Promise.all(
        filenames.map((filename) =>
          viewConfigService
            .getView(filename)
            .then((content) => ({
              id:
                content.id ||
                filename.replace(/^vue-/, "").replace(/\.json$/, ""),
              name: content.name || filename,
              description: content.description || "",
              scope: "global",
              filename,
              config: content.config || { locations: [], criteria: [] },
            }))
            .catch(() => null),
        ),
      );
      return views.filter((v) => v !== null);
    } catch (error) {
      console.error("Error loading global views from config:", error);
      const configStore = useConfigStore();
      return configStore.SAVED_VIEWS_GLOBAL || [];
    }
  }

  /**
   * Creates the hidden 'Configuration' folder in the user's home folder.
   * @param {string} homeFolderId - The ID of the user's home folder.
   * @returns {Promise<Object>} The created folder node object.
   */
  async createConfigFolder(homeFolderId) {
    const opts = {
      aspectNames: ["pm:hidden"],
      nodeType: "cm:folder",
      name: "Configuration",
    };

    try {
      return await alfrescoNodeService.createNode(homeFolderId, opts);
    } catch (error) {
      console.error("Error while creating the Configuration folder :", error);
      throw error;
    }
  }

  async _savePersonalView(viewToSave) {
    const user = useUserStore().person;
    if (!user) {
      throw new Error(t("vuesUtilisateurs.userNotFound"));
    }

    const hiddenFolder = await this._getHiddenFolder(user.id, true);
    const viewsFile = await this._getOrCreateViewsFile(hiddenFolder.id, true);

    let rawFileContent = await this._parseFileContent(viewsFile);
    let fileContent = this._normalizeFileContent(rawFileContent);

    const personalViews = fileContent.personalSavedView;

    if (personalViews.some((s) => s.id === viewToSave.id)) {
      throw new Error(
        t("vuesUtilisateurs.savedViewError.idAlreadyExists", {
          id: viewToSave.id,
        }),
      );
    }

    if (
      personalViews.some(
        (s) => s.name.toLowerCase() === viewToSave.name.toLowerCase(),
      )
    ) {
      throw new Error(
        t("vuesUtilisateurs.savedViewError.nameAlreadyExists", {
          id: viewToSave.name,
        }),
      );
    }

    personalViews.push(viewToSave);
    fileContent.personalSavedView = personalViews;

    const updatedContent = JSON.stringify(fileContent, null, 2);
    const blob = new Blob([updatedContent], { type: "application/json" });

    await alfrescoNodeService.updateContentNode(
      viewsFile.id,
      blob,
      "MAJEURE",
      null,
    );
  }

  /**
   * Saves a new personal view for the current user.
   * @param {Object} viewToSave - The view object to save.
   * @returns {Promise<void>}
   */
  async saveView(viewToSave) {
    if (viewToSave.scope === "global") {
      try {
        const currentConfig = await configService.getConfig(
          useConfigStore().INSTANCE_NAME,
          CONFIG_FILENAME,
        );
        const globalViews = currentConfig.jsonContent.SAVED_VIEWS_GLOBAL || [];

        if (globalViews.some((s) => s.id === viewToSave.id)) {
          throw new Error(
            t("vuesUtilisateurs.savedViewError.idAlreadyExists", {
              id: viewToSave.id,
            }),
          );
        }
        if (
          globalViews.some(
            (s) => s.name.toLowerCase() === viewToSave.name.toLowerCase(),
          )
        ) {
          throw new Error(
            t("vuesUtilisateurs.savedViewError.nameAlreadyExists", {
              id: viewToSave.name,
            }),
          );
        }

        globalViews.push(viewToSave);
        currentConfig.jsonContent.SAVED_VIEWS_GLOBAL = globalViews;

        await configService.updateConfig(
          useConfigStore().INSTANCE_NAME,
          CONFIG_FILENAME,
          currentConfig.jsonContent,
        );
      } catch (error) {
        console.error("Error while saving the global view :", error);
        throw error;
      }
    } else if (viewToSave.scope === "user") {
      await this._savePersonalView(viewToSave);
    }
  }
  async _updatePersonalView(viewToUpdate) {
    const user = useUserStore().person;
    if (!user) {
      throw new Error(t("vuesUtilisateurs.userNotFoundForUpdate"));
    }

    const hiddenFolder = await this._getHiddenFolder(user.id, true);
    const viewsFile = await this._getOrCreateViewsFile(hiddenFolder.id, true);

    let rawFileContent = await this._parseFileContent(viewsFile);
    let fileContent = this._normalizeFileContent(rawFileContent);

    const personalViews = fileContent.personalSavedView;

    if (
      personalViews.some(
        (s) =>
          s.id !== viewToUpdate.id &&
          s.name.toLowerCase() === viewToUpdate.name.toLowerCase(),
      )
    ) {
      throw new Error(
        t("vuesUtilisateurs.savedViewError.nameAlreadyExists", {
          id: viewToUpdate.name,
        }),
      );
    }

    const viewIndex = personalViews.findIndex((s) => s.id === viewToUpdate.id);
    if (viewIndex === -1) {
      throw new Error(
        t("vuesUtilisateurs.savedViewError.personnalNotFound", {
          id: viewToUpdate.id,
        }),
      );
    }

    personalViews[viewIndex] = viewToUpdate;
    fileContent.personalSavedView = personalViews;

    const updatedContent = JSON.stringify(fileContent, null, 2);
    const blob = new Blob([updatedContent], { type: "application/json" });
    return await alfrescoNodeService.updateContentNode(
      viewsFile.id,
      blob,
      "MAJEURE",
      null,
    );
  }

  /**
   * Updates an existing personal saved view.
   * @param {Object} viewToUpdate - The view object with updated information.
   * @returns {Promise<Object>} The updated node object from Alfresco.
   */
  async updateView(viewToUpdate) {
    if (viewToUpdate.scope === "global") {
      try {
        const currentConfig = await configService.getConfig(
          useConfigStore().INSTANCE_NAME,
          CONFIG_FILENAME,
        );
        const globalViews = currentConfig.jsonContent.SAVED_VIEWS_GLOBAL || [];

        if (
          globalViews.some(
            (s) =>
              s.id !== viewToUpdate.id &&
              s.name.toLowerCase() === viewToUpdate.name.toLowerCase(),
          )
        ) {
          throw new Error(
            t("vuesUtilisateurs.savedViewError.nameAlreadyExists", {
              id: viewToUpdate.name,
            }),
          );
        }

        const viewIndex = globalViews.findIndex(
          (s) => s.id === viewToUpdate.id,
        );
        if (viewIndex === -1) {
          throw new Error(
            t("vuesUtilisateurs.savedViewError.globalNotFound", {
              id: viewToUpdate.id,
            }),
          );
        }

        globalViews[viewIndex] = viewToUpdate;
        currentConfig.jsonContent.SAVED_VIEWS_GLOBAL = globalViews;

        await configService.updateConfig(
          useConfigStore().INSTANCE_NAME,
          CONFIG_FILENAME,
          currentConfig.jsonContent,
        );
        return viewToUpdate;
      } catch (error) {
        console.error("Error while updating the global view :", error);
        throw error;
      }
    } else if (viewToUpdate.scope === "user") {
      return await this._updatePersonalView(viewToUpdate);
    }
  }
  async _deletePersonalView(viewToDelete) {
    const user = useUserStore().person;
    if (!user) {
      throw new Error(t("vuesUtilisateurs.userNotFoundForDelete"));
    }

    const hiddenFolder = await this._getHiddenFolder(user.id, false);
    if (!hiddenFolder) {
      throw new Error(t("vuesUtilisateurs.configFolderNotFound"));
    }

    const viewsFile = await this._getOrCreateViewsFile(hiddenFolder.id, false);
    if (!viewsFile) {
      throw new Error(t("vuesUtilisateurs.configFileNotFound"));
    }

    let rawFileContent = await this._parseFileContent(viewsFile);
    let fileContent = this._normalizeFileContent(rawFileContent);

    const personalViews = fileContent.personalSavedView;
    fileContent.personalSavedView = personalViews.filter(
      (s) => s.id !== viewToDelete.id,
    );

    const updatedContent = JSON.stringify(fileContent, null, 2);
    const blob = new Blob([updatedContent], { type: "application/json" });
    await alfrescoNodeService.updateContentNode(
      viewsFile.id,
      blob,
      "MAJEURE",
      null,
    );
  }

  /**
   * Deletes a personal saved view.
   * @param {Object} viewToDelete - The view object to delete.
   * @returns {Promise<void>}
   */
  async deleteView(viewToDelete) {
    if (viewToDelete.scope === "global") {
      try {
        const currentConfig = await configService.getConfig(
          useConfigStore().INSTANCE_NAME,
          CONFIG_FILENAME,
        );
        const globalViews = currentConfig.jsonContent.SAVED_VIEWS_GLOBAL || [];
        currentConfig.jsonContent.SAVED_VIEWS_GLOBAL = globalViews.filter(
          (s) => s.id !== viewToDelete.id,
        );
        await configService.updateConfig(
          useConfigStore().INSTANCE_NAME,
          CONFIG_FILENAME,
          currentConfig.jsonContent,
        );
      } catch (error) {
        console.error("Error while deleting the global view :", error);
        throw error;
      }
      return;
    } else if (viewToDelete.scope === "user") {
      await this._deletePersonalView(viewToDelete);
    }
  }
  /**
   * Retrieves a specific saved view by its ID and scope.
   * @param {'user'|'global'} scope - The scope of the view.
   * @param {string} viewId - The ID of the view to retrieve.
   * @returns {Promise<Object|null>} The found view object, or null.
   */
  async getViewById(scope, viewId) {
    if (scope === "user") {
      const personalViews = await this.getPersonalViews();
      return personalViews.find((s) => s.id === viewId) || null;
    }

    if (scope === "global") {
      const globalViews = await this.getGlobalViews();
      return globalViews.find((s) => s.id === viewId) || null;
    }

    console.error(`Unknown view scope: ${scope}`);
    return null;
  }

  /**
   * Toggles the favorite status of a saved view.
   * For personal views, it updates the `isFavorite` flag on the view object itself.
   * For global views, it adds/removes a reference in the user's personal `globalFavoriteView` list.
   * @param {Object} viewToEdit - The view object to toggle.
   * @returns {Promise<Object>} The updated view object.
   */
  async toggleFavoriteStatus(viewToEdit) {
    if (viewToEdit.scope === "global") {
      const user = useUserStore().person;
      if (!user) {
        throw new Error(t("vuesUtilisateurs.userNotFound"));
      }

      const hiddenFolder = await this._getHiddenFolder(user.id, true);
      const viewsFile = await this._getOrCreateViewsFile(hiddenFolder.id, true);

      let rawFileContent = await this._parseFileContent(viewsFile);
      let fileContent = this._normalizeFileContent(rawFileContent);

      const globalFavorites = fileContent.globalFavoriteView;
      const favoriteIndex = globalFavorites.findIndex(
        (fav) => fav.id === viewToEdit.id,
      );

      if (favoriteIndex > -1) {
        globalFavorites.splice(favoriteIndex, 1);
        viewToEdit.isFavorite = false;
      } else {
        globalFavorites.push({ id: viewToEdit.id, name: viewToEdit.name });
        viewToEdit.isFavorite = true;
      }
      fileContent.globalFavoriteView = globalFavorites;

      const updatedContent = JSON.stringify(fileContent, null, 2);
      const blob = new Blob([updatedContent], { type: "application/json" });
      await alfrescoNodeService.updateContentNode(
        viewsFile.id,
        blob,
        "MAJEURE",
        null,
      );

      return viewToEdit;
    } else {
      viewToEdit.isFavorite = !viewToEdit.isFavorite;
      return await this.updateView(viewToEdit);
    }
  }
}
export default new UserViewService();
